/* =====================================================================================
   Atlas helpers and constants, with no data attached.

   Split out because Vite bundles a module, not a symbol. Atlas.tsx imports siteTotal() and
   organLabel(); when those lived beside ALL_SITES, importing a two-line helper pulled 807
   transplant centres and every national waiting list into the bundle. That is invisible in
   the transplant build and unacceptable in a white-label one, where it put Scira's market
   model inside a pharmaceutical customer's demonstration.

   Nothing in this file may import data. That is the whole point of it.
   ===================================================================================== */

import type { Site, Organ, SegmentKey } from './atlas';

/* Prevalent grafts per incident transplant, calibrated against published national totals.

   KNOWN LIMITATION, stated rather than buried: distributing by current volume assumes a
   centre's share of national activity has been roughly stable. A programme that doubled in
   the last three years is over-credited; one that has shrunk is under-credited. Field
   intelligence should correct individual centres, and an employee's correction must never be
   overwritten by this calculation.
   ===================================================================================== */
export const GRAFT_YEARS: Record<string, number> = {
  /* 9.75, not 8.44. The earlier figure divided the USRDS count by the sum of volume across the
     254 US CENTRES in the seed (32,363); the Atlas applies the multiplier to the NATIONAL
     figure (28,000). Calibrating against one denominator and dividing by another understated
     the United States by 13 per cent. 273,041 / 28,000 = 9.75.

     This is still the LOW end of what the published anchors show: the United Kingdom implies
     12.68 and Australia 13.65, because their programmes grew more slowly and therefore carry
     more living recipients per current transplant. See data/atlas.prevalence.ts. */
  kidney: 9.75,
  kidney_pancreas: 9.75,   // an SPK produces a functioning kidney graft and USRDS counts it

  /* MODELLED from published graft survival, with NO published prevalence anywhere to calibrate
     against for any of them. That is worth stating plainly: the kidney figure is now anchored
     for nine countries and 53 per cent of its total, while every liver, heart and lung figure
     below rests entirely on survival curves reduced by a factor.

     RESCALED BY 9.75/8.44 ON 7 AUGUST 2026. The reduction factor was fitted against the old
     kidney calibration of 8.44. Correcting that to 9.75, which is the same quantity on the
     denominator the Atlas actually uses, left these internally inconsistent with the one
     number in the set that has a published anchor behind it. They are scaled to restore the
     relationship rather than left at values that no longer follow from their own derivation.

     They remain 'estimated' and must not be quoted as though they carried the kidney figure's
     evidence. */
  liver: 8.20,
  heart: 9.01,
  lung: 5.03,            // median lung graft survival is materially shorter
  cardiothoracic: 7.05,  // NHSBT reports heart and lung as one figure; blended
  pancreas: 7.05,
  intestine: 5.03,
  hsct_allo: 0,          // not a solid organ graft; excluded rather than guessed at
  hsct_auto: 0,
};

/* SCIRA'S LABELS, not the product's. Kept here because the transplant seed and the Scira
   tenant both want them, but no shared module may read this map: Atlas and SitePage take their
   segment labels from TENANT.vocabulary.segment.labels, which is where a tenant that segments
   by joint or by tumour type supplies its own. */
export const organLabel: Record<Organ, string> = {
  kidney: 'Kidney', liver: 'Liver', heart: 'Heart', lung: 'Lung',
  pancreas: 'Pancreas', intestine: 'Intestine',
  // Combined reporting categories. Named as the registry names them, because relabelling a
  // combined figure as one of its parts is how a map starts telling small lies.
  kidney_pancreas: 'Kidney-pancreas', heart_lung: 'Heart-lung',
  cardiothoracic: 'Cardiothoracic (combined)',
  hsct_allo: 'HSCT (allo)', hsct_auto: 'HSCT (auto)',
};


/* `exclude` is how a tenant says "this segment is real but must not be added into the headline".
   Scira excludes allogeneic HSCT, which is not a solid organ graft and would inflate a
   transplant total. Defaulted to that so the transplant behaviour is unchanged, but it is a
   parameter rather than a constant because the next tenant will have its own version of a
   category that exists and does not belong in the sum. */
/* Number.isFinite does not narrow a `number | undefined` for the compiler, so the guards that
   have always been in the map read as unsafe the moment coordinates became optional. This says
   the same thing in a form TypeScript understands, rather than scattering non-null assertions
   through the projection where a genuine mistake would then be invisible. */
export function placed<T extends { lat?: number; lon?: number }>(
  s: T): s is T & { lat: number; lon: number } {
  return Number.isFinite(s.lat) && Number.isFinite(s.lon);
}

export function siteTotal(s: Site, segment?: SegmentKey, exclude: string[] = ['hsct_allo']): number {
  return s.volumes
    .filter((v) => (segment ? v.organ === segment : !exclude.includes(v.organ)))
    .reduce((a, v) => a + v.total, 0);
}
